import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FiMenu, FiX, FiShoppingBag, FiTool, FiHome, FiUser, 
  FiLogOut, FiInfo, FiMail, FiShoppingCart, FiSearch,
  FiHeart, FiClock, FiXCircle
} from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { API_ENDPOINTS } from '../utils/constants';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const { isAuth, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Telegram username for contact
  const TELEGRAM_USERNAME = 'Bossasean168';
  const WEBSITE_URL = 'https://asianphone.vercel.app';

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch search suggestions from API
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length > 0) {
        try {
          const response = await api.get(API_ENDPOINTS.PRODUCTS, {
            params: { 
              search: searchQuery,
              limit: 8
            }
          });
          const products = response.data.data || response.data;
          const suggestions = products.map(product => product.title);
          setSearchSuggestions(suggestions.slice(0, 8));
          setShowSuggestions(true);
          setSelectedSuggestionIndex(-1);
        } catch (err) {
          console.error('Error fetching suggestions:', err);
          // Fallback to local suggestions if API fails
          const localSuggestions = [
            'iPhone 15 Pro Max',
            'iPhone 15 Pro',
            'iPhone 14 Pro Max',
            'iPhone 14',
            'iPhone 13',
            'Samsung Galaxy S24 Ultra',
            'Samsung Galaxy S23',
            'Xiaomi 14 Ultra',
            'Xiaomi Redmi Note 13',
            'Oppo Reno 11',
            'Vivo V30'
          ].filter(product => 
            product.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setSearchSuggestions(localSuggestions.slice(0, 8));
          setShowSuggestions(true);
        }
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('ចាកចេញដោយជោគជ័យ');
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
      setIsOpen(false);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    navigate(`/products?search=${encodeURIComponent(suggestion)}`);
    setIsSearchOpen(false);
    setIsOpen(false);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (searchSuggestions.length === 0) return;

    // Arrow Down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < searchSuggestions.length - 1 ? prev + 1 : prev
      );
    }
    // Arrow Up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    }
    // Enter
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && searchSuggestions[selectedSuggestionIndex]) {
        handleSuggestionClick(searchSuggestions[selectedSuggestionIndex]);
      } else {
        handleSearch(e);
      }
    }
    // Escape
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleTelegramContact = () => {
    const message = encodeURIComponent(
      `សួស្តី! ខ្ញុំចាប់អារម្មណ៍ចង់ទិញទូរស័ព្ទពីហាងអ្នក។\n\n` +
      `សូមផ្តល់ព័ត៌មានផលិតផល និងតម្លៃផង។\n\n` +
      `អរគុណ!`
    );
    window.open(`https://t.me/${TELEGRAM_USERNAME}?text=${message}`, '_blank');
    toast.success('បើក Telegram ដើម្បីទំនាក់ទំនង');
  };

  const navLinks = [
    { name: 'ទំព័រដើម', path: '/', icon: FiHome },
    { name: 'ទូរស័ព្ទ', path: '/products', icon: FiShoppingBag },
    { name: 'ជួសជុល', path: '/repairs', icon: FiTool },
    { name: 'អំពីយើង', path: '/about', icon: FiInfo },
    { name: 'ទំនាក់ទំនង', path: '/contact', icon: FiMail },
  ];

  const bottomNavItems = [
    { name: 'ទំព័រដើម', path: '/', icon: FiHome },
    { name: 'ទូរស័ព្ទ', path: '/products', icon: FiShoppingBag },
    { name: 'ជួសជុល', path: '/repairs', icon: FiTool },
    { name: 'ទំនាក់ទំនង', path: '/contact', icon: FiMail },
  ];

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/logo3.png" 
                alt="Asian Phone"
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo3.png";
                }}
              />
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    isActivePath(link.path)
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Desktop Search Bar with Dropdown */}
              <div className="hidden lg:block relative" ref={searchRef}>
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                    placeholder="ស្វែងរកទូរស័ព្ទ..."
                    className="w-80 px-4 py-2 border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:outline-none focus:border-red-600 focus:bg-white"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setShowSuggestions(false);
                        inputRef.current?.focus();
                      }}
                      className="absolute right-10 text-gray-400 hover:text-red-600"
                    >
                      <FiXCircle size={16} />
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-200 text-gray-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                  >
                    <FiSearch size={18} />
                  </button>
                </form>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-80 overflow-y-auto">
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                        className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors border-b border-gray-100 last:border-0 ${
                          selectedSuggestionIndex === index ? 'bg-red-50 text-red-600' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FiSearch size={14} className="text-gray-400" />
                          <span>{suggestion}</span>
                        </div>
                      </button>
                    ))}
                    <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                      ចុច Enter ដើម្បីស្វែងរកទាំងអស់
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Search Icon */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
              >
                <FiSearch size={20} />
              </button>

              {/* Buy Now Button - Opens Telegram */}
              <button
                onClick={handleTelegramContact}
                className="hidden lg:flex items-center gap-2 bg-red-600 text-white px-4 py-2 font-medium text-sm hover:bg-red-700 transition-colors"
              >
                <FaTelegramPlane size={16} />
                <span>ទិញឥឡូវនេះ</span>
              </button>

              {/* User Menu - Desktop (Only show if logged in) */}
              {isAuth && (
                <div className="hidden md:flex items-center">
                  <Link 
                    to="/profile" 
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
                    title="Profile"
                  >
                    <FiUser size={20} />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
                    title="Logout"
                  >
                    <FiLogOut size={20} />
                  </button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
                aria-label="Toggle menu"
              >
                {isOpen ? <FiX size={20} /> : <FiMenu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Search Bar with Dropdown */}
          <div 
            className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
              isSearchOpen ? 'max-h-96 py-3' : 'max-h-0'
            }`}
          >
            <div className="relative">
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="ស្វែងរកទូរស័ព្ទ..."
                  className="flex-1 px-4 py-2 border border-gray-200 bg-gray-50 text-gray-700 text-sm focus:outline-none focus:border-red-600"
                  autoFocus={isSearchOpen}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-12 text-gray-400 hover:text-red-600"
                  >
                    <FiXCircle size={16} />
                  </button>
                )}
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-gray-100 border border-l-0 border-gray-200 text-gray-600 hover:bg-red-600 hover:text-white"
                >
                  <FiSearch size={18} />
                </button>
              </form>

              {/* Mobile Search Suggestions */}
              {isSearchOpen && showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-60 overflow-y-auto">
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors border-b border-gray-100 last:border-0 ${
                        selectedSuggestionIndex === index ? 'bg-red-50 text-red-600' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FiSearch size={14} className="text-gray-400" />
                        <span>{suggestion}</span>
                      </div>
                    </button>
                  ))}
                  <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                    ចុច Enter ដើម្បីស្វែងរកទាំងអស់
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu - Slides from right */}
        <div 
          className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
            isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          onClick={() => setIsOpen(false)}
        >
          <div 
            className={`absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white shadow-xl transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="font-bold text-gray-800">ម៉ឺនុយ</span>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-red-50 hover:text-red-600"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4">
              {/* Mobile Navigation Links */}
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      isActivePath(link.path)
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-700 hover:bg-red-50 hover:text-red-600'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <link.icon className="text-lg" />
                    <span className="font-medium">{link.name}</span>
                  </Link>
                ))}
              </div>

              <div className="h-px bg-gray-200 my-4" />

              {/* Mobile Action Buttons */}
              <button
                onClick={() => {
                  handleTelegramContact();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-3 font-medium hover:bg-red-700 mb-3"
              >
                <FaTelegramPlane size={18} />
                <span>ទិញឥឡូវនេះ</span>
              </button>

              {/* Mobile User Section */}
              {isAuth ? (
                <>
                  <div className="px-4 py-3 bg-gray-50 mb-2">
                    <div className="font-medium text-gray-800">
                      {user?.email?.split('@')[0]}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600"
                  >
                    <FiLogOut className="text-lg" />
                    <span className="font-medium">ចាកចេញ</span>
                  </button>
                </>
              ) : (
                <div className="text-center text-sm text-gray-500">
                  <p>ទំនាក់ទំនងតាម Telegram</p>
                  <p className="text-red-600 mt-1">@Bossasean168</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 md:hidden shadow-lg">
          <div className="flex items-center justify-around h-14">
            {bottomNavItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.path);
              
              return (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                    isActive 
                      ? 'text-red-600' 
                      : 'text-gray-500 hover:text-red-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={20} />
                  <span className="text-xs mt-1 font-medium">{item.name}</span>
                  
                  {/* Active Indicator */}
                  {isActive && (
                    <span className="absolute bottom-0 w-8 h-0.5 bg-red-600"></span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Add padding to bottom of page content to account for bottom nav on mobile */}
      {isMobile && <div className="" />}
    </>
  );
};

export default Navbar;
